<template>
    <vue-custom-scrollbar
        v-if="!isLoading"
        class="app"
        tagname="div"
        :settings="scrollSettings"
    >
        <header id="header" class="header" :class="{ hide: previewMode }">
            <button
                v-on-clickaway="onMenuClickAway"
                class="header__icon header__icon--left"
                type="button"
                title="Menu"
                @click="isMenuExpanded = !isMenuExpanded"
            >
                <font-awesome-icon :icon="['fal', 'bars']" />
                <site-menu v-show="isMenuExpanded" />
            </button>

            <app-logo class="header__logo" @dblclick="toggleAppTheme" />

            <header-breadcrumb />

            <div class="header__nav">
                <nav class="nav">
                    <ul class="nav__list">
                        <li class="nav__item">
                            <a href="/editor" class="nav__link" :class="routeActive('editor')">
                                PAGE EDITOR
                            </a>
                        </li>

                        <li v-if="user.admin" class="nav__item">
                            <a href="/templates" class="nav__link" :class="routeActive('templates')">
                                TEMPLATE EDITOR
                            </a>
                        </li>

                        <li v-if="user.designer" class="nav__item">
                            <a href="/components" class="nav__link" :class="routeActive('components')">
                                COMPONENT EDITOR
                            </a>
                        </li>

                        <li class="nav__item">
                            <a
                                :href="cmUrl"
                                rel="noopener noreferrer"
                                class="nav__link"
                                target="_blank"
                            >
                                CONTENT EDITOR
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>

            <button
                v-if="!routeActive('components') && !routeActive('settings')"
                class="header__icon header__icon--right"
                type="button"
                title="Content Browser"
                @click="showContentNav"
            >
                <font-awesome-icon :icon="['fal', 'folder-tree']" />
            </button>
        </header>

        <toolbar-preview v-if="previewMode" />
        <component-context-menu />
        <component-list-context-menu />

        <transition
            enter-active-class="animated fadeIn"
            leave-active-class="animated fadeOut"
            mode="out-in"
        >
            <router-view class="flex-view" />
        </transition>

        <component :is="drawerComponent" v-if="drawerComponent" />
        <component :is="modalComponent" v-if="modalComponent" :modal-data="modalData" />

        <alert v-if="alert" />
        <prompt v-if="prompt" />

        <splash :is-loading="!isAuthenticated" />

        <transition appear name="toolbar-fade">
            <toolbar-element
                v-if="isToolbarLoaded"
                v-show="isToolbarVisible"
                @cut="onElementCut"
                @copy="onElementCopy"
                @move="onElementMove"
                @delete="onElementDelete"
            />
        </transition>
    </vue-custom-scrollbar>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import 'vue-custom-scrollbar/dist/vueScrollbar.css';
import { mixin as clickaway } from 'vue-clickaway';

import HeaderBreadcrumb from '@/components/page-editor/shared/header-breadcrumb/header-breadcrumb';
import SiteMenu from '@/components/shared/site-menu';
import Splash from '@/components/utilities/Splash';
import AppLogo from '@/components/shared/app-logo';
import ToolbarElement from '@/components/page-editor/toolbars/toolbar-element';
import { getDevices } from '@/utils/devices';
import {
    CLEAR_COPIED_RENDERING,
    CLEAR_SELECTED_ELEMENTS,
    CLEAR_SELECTED_RENDERING,
    SET_DEVICE_MODE,
    SET_PREVIEW_MODE,
    SET_CHANGED_DATA,
    SET_APP_THEME,
    SET_APP_THEME_LIST,
    SET_USER,
    SET_PROMPT_TEMPLATE,
    UPDATE_TOKEN,
    SET_HOLD_EVENTS,
    TOGGLE_PAGE_LOADING,
    SHOW_DRAWER,
    HIDE_SNACKBAR,
} from '@/store/types/mutation-types';
import { LOAD_COMPONENT_TREE, GET_USER } from '@/store/types/action-types';
import { STATE_CLASSES } from '@/data/constants/classes';
import appThemes from '@/data/app-themes';
import mapFilters from '@/utils/map-filters';

export default {
    name: 'app',

    components: {
        AppLogo,
        HeaderBreadcrumb,
        SiteMenu,
        Splash,
        ToolbarElement,
        vueCustomScrollbar,

        // popups
        ComponentContextMenu: () => import('@/components/shared/context-menus/component-context-menu'),
        ComponentListContextMenu: () => import('@/components/shared/context-menus/component-list-context-menu'),
        ToolbarPreview: () => import('@/components/page-editor/toolbars/toolbar-preview'),

        // drawers
        ContentHubDrawer: () => import('@/components/page-editor/content-hub-drawer'),
        CreateFolderDrawer: () => import('@/components/page-editor/nav-drawers/create-folder-drawer'),
        CreateMediaFolder: () => import('@/components/page-editor/nav-drawers/create-media-folder'),
        CreatePageDrawer: () => import('@/components/page-editor/nav-drawers/create-page-drawer'),
        DrawerFlexEvents: () => import('@/components/events/drawer-flex-events'),
        LayoutDrawer: () => import('@/components/page-editor/layout-drawer'),
        LoadTemplateDrawer: () => import('@/components/page-editor/nav-drawers/load-template-drawer'),
        MediaUploadDrawer: () => import('@/components/page-editor/nav-drawers/media-upload-drawer'),
        MetadataEditor: () => import('@/components/page-editor/metadata-editor'),
        NavigationTreeContent: () => import('@/components/page-editor/navigation-tree/navigation-tree-content'),
        NavigationTreeContentSelector: () => import('@/components/page-editor/navigation-tree/navigation-tree-content-selector'),
        NavigationTreeMedia: () => import('@/components/page-editor/navigation-tree/navigation-tree-media'),
        TaxonomyEditor: () => import('@/components/page-editor/taxonomy-editor'),

        // modals
        Alert: () => import('@/components/shared/modal/alert'),
        AddFolderModal: () => import('@/components/component-editor/modal-add-folder'),
        ComponentModalSave: () => import('@/components/component-editor/modal-save'),
        LanguageModal: () => import('@/components/page-editor/language-modal'),
        Prompt: () => import('@/components/shared/modal/prompt'),
        ModalDynamicOptions: () => import('@/components/component-editor/modal-dynamic-options'),
        ModalHtmlCustomAttributes: () => import('@/components/component-editor/modal-html-custom-attributes'),
        ModalInputOptions: () => import('@/components/component-editor/modal-input-options'),
        RichTextModal: () => import('@/components/shared/modal/rich-text-modal'),
    },

    mixins: [clickaway],

    data() {
        return {
            boxModelDisplayed: false,
            isLoading: true,
            isMenuExpanded: false,
            scrollSettings: {
                suppressScrollX: true,
            },
            showPageNav: false,
            STATE_CLASSES,
        };
    },

    computed: {
        ...mapState({
            alert: state => state.alert,
            appTheme: state => state.page.appTheme,
            appThemeList: state => state.page.appThemeList,
            changedData: state => state.page.changedData,
            confirmedLeave: state => state.page.confirmedLeave,
            drawer: state => state.page.drawer,
            guidedMode: state => state.page.guidedMode,
            isAuthenticated: state => state.user.isAuthenticated,
            lastComponentId: state => state.page.lastComponentId,
            lastPageId: state => state.page.lastPageId,
            modal: state => state.page.modal,
            pageData: state => state.page.pageData,
            previewMode: state => state.previewMode,
            prompt: state => state.prompt,
            selectedElements: state => state.page.selectedElements,
            user: state => state.user.user,
            holdEvents: state => state.page.holdEvents,
        }),

        cmUrl() {
            const cmurl = `${ process.env.VUE_APP_CM_URL }/sitecore/shell/sitecore/client/applications/launchpad`;
            return cmurl;
        },

        isToolbarLoaded() {
            return !!this.selectedElements.length
                && this.selectedElements[0].elementType !== 'flex-layout'
                && this.$route.name !== 'settings';
        },

        isToolbarVisible() {
            return !this.selectedElements[0].boxModelDisplayed
                && !this.guidedMode
                && !this.previewMode;
        },

        drawerComponent() {
            return this.drawer ? this.drawer.componentTag : null;
        },

        modalComponent() {
            return this.modal ? this.modal.componentTag : null;
        },

        modalData() {
            return this.modal ? this.modal.data : null;
        },
    },

    watch: {
        $route: {
            handler() {
                this.clearSelectedRendering();
            },
            deep: true,
        },
    },

    created() {
        const defaultScreen = getDevices()[0];

        window.updateUser = this.updateUser;

        this.loadComponentTree();
        this.setDeviceMode(defaultScreen);
        this.setPreviewMode(false);

        this.setUnloadNotSavedListener();
        this.setKeyboardListeners();
        this.initTheme();

        this.setPromptTemplate({
            name: 'unsaved-changes',
            template: {
                title: 'You have unsaved changes',
                description: 'Any unsaved changes will be lost. Do you want to continue?',
            },
        });
    },

    async mounted() {
        this.$el.addEventListener('click', this.enableEvents);
        this.$el.addEventListener('onkeydown', this.enableEvents);

        await this.$nextTick();

        this.isLoading = false;
    },

    beforeDestroy() {
        this.$el.removeEventListener('click', this.enableEvents);
        this.$el.removeEventListener('onkeydown', this.enableEvents);
    },

    methods: {
        ...mapFilters(['camelCase']),

        ...mapActions({
            loadComponentTree: LOAD_COMPONENT_TREE,
            getUser: GET_USER,
        }),

        ...mapMutations({
            clearCopiedRendering: CLEAR_COPIED_RENDERING,
            clearSelectedRendering: CLEAR_SELECTED_RENDERING,
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
            hideSnackbar: HIDE_SNACKBAR,
            setAppTheme: SET_APP_THEME,
            setAppThemeList: SET_APP_THEME_LIST,
            setDeviceMode: SET_DEVICE_MODE,
            setPreviewMode: SET_PREVIEW_MODE,
            setChangedData: SET_CHANGED_DATA,
            setPromptTemplate: SET_PROMPT_TEMPLATE,
            setUser: SET_USER,
            showDrawer: SHOW_DRAWER,
            updateToken: UPDATE_TOKEN,
            setHoldEvents: SET_HOLD_EVENTS,
            togglePageLoading: TOGGLE_PAGE_LOADING,
        }),

        initTheme() {
            this.setAppThemeList(Object.keys(appThemes));

            const savedAppTheme = localStorage.getItem('flexAppTheme');
            const appTheme = this.appThemeList.includes(savedAppTheme) ? savedAppTheme : 'dark';

            this.setAppTheme(appTheme);
        },

        enableEvents() {
            if (this.holdEvents) {
                this.setHoldEvents(false);
            }
        },

        setUnloadNotSavedListener() {
            const { NODE_ENV, VUE_APP_ENV } = process.env;

            if (NODE_ENV !== 'development' || VUE_APP_ENV === 'connected') {
                window.addEventListener('beforeunload', (e) => {
                    if (!this.changedData) return;

                    e.preventDefault();
                    e.returnValue = '';
                });
            }
        },

        setKeyboardListeners() {
            this.$keydown(['ctrl', 'shift', 't'], this.toggleAppTheme);

            this.$keydown('escape', [
                this.clearCopiedRendering,
                this.clearSelectedElements,
                this.closeContextMenus,
                this.hideSnackbar,
                this.hideBoxModel,
            ]);

            this.$keydown(['ctrl', 'shift', 'm'], this.showBoxModel);
            this.$keyup(['ctrl', 'shift', 'm'], this.hideBoxModel);
        },

        showContentNav() {
            switch (this.$route.name) {
                case 'templates':
                    this.showDrawer('load-template-drawer');
                    break;

                case 'components':
                    // do nothing
                    break;

                default:
                    this.showDrawer('navigation-tree-content');
            }
        },

        showBoxModel() {
            if (this.guidedMode) return;

            this.selectedElements.forEach((selectedElement) => {
                selectedElement.boxModelDisplayed = true;
            });

            this.boxModelDisplayed = true;
        },

        hideBoxModel() {
            if (!this.boxModelDisplayed) return;

            this.selectedElements.forEach((selectedElement) => {
                selectedElement.boxModelDisplayed = false;
            });

            this.boxModelDisplayed = false;
        },

        closeContextMenus() {
            this.$root.$emit('flex::context::close');
        },

        toggleAppTheme() {
            const theme = this.appTheme === this.appThemeList[0]
                ? this.appThemeList[1]
                : this.appThemeList[0];

            this.setAppTheme(theme);
        },

        routeActive(name) {
            if (this.$route.name === name) {
                return STATE_CLASSES.SELECTED;
            }
            return '';
        },

        async updateUser() {
            // this.updateToken({ token: user.access_token, expiresAt: user.expires_at });
            const storeuser = await this.getUser();
            if (storeuser) {
                this.setUser({ user: storeuser });
            }
        },

        testSignin() {
            window.location.href = '/signin';
        },

        onElementCut() {
            this.selectedElements[0].$emit('cut');
        },

        onElementCopy() {
            this.selectedElements[0].$emit('copy');
        },

        onElementMove(direction) {
            const selected = this.selectedElements[0];

            selected.$emit('move-element', direction, selected.index);
        },

        onElementDelete() {
            const selected = this.selectedElements[0];

            if (selected.group.length) {
                selected.group.splice(selected.group.indexOf(selected), 1);
            }

            selected.$emit('delete-element', selected.index);
        },

        onMenuClickAway() {
            this.isMenuExpanded = false;
        },
    },
};
</script>

<style lang="scss">
@import "@/styles/_includes.scss";

.fc-display-contents {
    /* autoprefixer: ignore next */
    display: contents;
}

.is-editing .#{ $is-selected-element } {
    outline: 2px dashed var(--app-selectedBorderColor) !important;

    &.#{ $is-invalid } {
        outline-color: $attention-color !important;
    }
}

.#{ $is-highlighted }.#{ $is-selected-element } {
    outline: 2px dashed var(--app-linkColor) !important;
}

.#{ $is-static } {
    pointer-events: none;
}

.#{ $is-preview } {
    .#{ $is-static }{
        pointer-events: all;
    }
}

.#{ $is-locked } {
    pointer-events: none;

    .always-enabled {
        pointer-events: none;
    }
}

.#{ $is-preview } {
    .#{ $is-locked }{
        pointer-events: all;
    }
}

.#{ $is-preview } .ps {
    overflow: visible !important; // needed else vue-custom-scrollbar causes 100vh to not work properly while in preview mode
}

@include option-stripes(rgba($hidden-stripe-color, 0.33), transparent, $hidden-text-color, HIDDEN, f-hidden);

// need to duplicate the selectors to override the ones output by the stripes() mixin
.#{ $is-hidden } .f-hidden.f-hidden.f-hidden.f-hidden.f-hidden { display: none !important; }
.#{ $is-hidden }.#{ $is-laptop } .f-hidden.f-hidden.f-hidden.lap\:f-block { display: block !important; }
.#{ $is-hidden }.#{ $is-tablet } .f-hidden.f-hidden.f-hidden.tab\:f-block { display: block !important; }
.#{ $is-hidden }.#{ $is-mobile } .f-hidden.f-hidden.f-hidden.mob\:f-block { display: block !important; }

@include option-stripes(rgba($disabled-stripe-color, 0.33), transparent, $disabled-text-color, DISABLED, is-disabled);
// need to duplicate the selectors to override the ones output by the stripes() mixin
.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled } {
    display: none !important;
}

.component-editor .#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled } {
    display: block !important;
}

.is-preview .component-editor .#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled },
.is-hidden .component-editor .#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled }.#{ $is-disabled } {
    display: none !important;
}

.#{ $is-highlighted } {
    outline: 2px dashed var(--app-highlightedBorderColor) !important;

    .drop-zone {
        min-height: 100px;
    }

    .#{ $is-preview } & {
        outline: none !important;
    }
}

.#{ $is-locked } {
    .drop-zone {
        display: none;
    }
}

.app-components-enter-active,
.app-components-leave-active {
    transition: all 0.5s;
}

.app-components-enter {
    opacity: 0;
    transform: translateY(-30px);
}

.app-components-leave-to {
    opacity: 0;
    transform: translateY(-30px);
}

.fadeIn {
    animation-duration: 0.3s;
}

.fadeOut {
    animation-duration: 0.3s;
}

.v-popper__popper {
    z-index: 9999;

    .v-popper__inner {
        padding: 10px;
    }
}

.toolbar-fade {
    &-enter-active {
      transition: opacity .3s ease;
    }

    &-enter,
    &-leave-to {
      opacity: 0;
    }
}

// detect if an image atom is a sibling to one or more other elements
// if so, we want to move its guided select toolbar to the right, so it doesn't get overlapped
[data-image-atom]:first-child:not(:last-child),
[data-image-atom]:last-child:not(:first-child),
[data-image-atom]:not(:first-child):not(:last-child) {
    .gs-toolbar {
        left: auto;
        right: 0;
    }
}

.app {
    font-family: $default-font-family;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: var(--app-color);
    background-color: var(--app-backgroundColor);
    min-height: 100vh;

    .#{ $is-preview } & {
        color: inherit;
        background-color: #fff;
    }
}

.header {
    $this: &;
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    color: var(--navbar-color);
    background-image: linear-gradient(var(--navbar-backgroundColorTop), var(--navbar-backgroundColorBottom));
    padding: 0 12px;
    align-items: center;
    transition: transform 0.45s;
    height: $header-height;

    &.hide {
        position: fixed;
        transform: translateY(-100%);
    }

    &__logo {
        max-height: 28px;
    }

    &__nav {
        margin-left: auto;
    }

    &__icon {
        display: flex;
        align-items: center;
        background-color: transparent;
        border: none;

        &--left {
            padding: 5px 10px;
            margin: 0 15px 0 0;
            font-size: 20px;
        }

        &--right {
            margin: 0 15px;
            font-size: 18px;
            color: #fff;
        }
    }
}

.nav {
    align-items: center;

    &__list {
        margin: 0;
        padding: 0;
        display: flex;
    }

    &__item {
        display: flex;
        list-style: none;
    }

    &__link {
        position: relative;
        color: var(--navbar-linkColor);
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.03em;
        padding: 12px 25px 11px;
        text-decoration: none;

        &:hover {
            background-color: var(--navbar-linkHoverBackgroundColor);
            text-decoration: none !important;
            color: var(--navbar-linkHoverColor);
            cursor: pointer;
        }

        &:before {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 3px;
            background-color: var(--navbar-linkColor);
            transition: transform 0.45s;
            transform: scaleX(0);
        }

        &.#{ $is-selected } {
            pointer-events: none;
        }

        &.#{ $is-selected }:before {
            transform: scaleX(1);
        }

        img {
            display: block;
            width: 16px;
            position: relative;
            top: 3px;
        }
    }
}

.flex-error-box {
    width: 600px;
    height: 200px;
}

.flex-view {
    padding-top: $header-height;

    .#{ $is-preview } & {
        padding-top: 0;
    }
}
</style>
